import React, { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Scale, CheckCircle, ArrowRight, Clock, Lock } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { classNames, isPremiumUser } from "@/utils/helpers";
interface LegalAssistanceSheetProps {
  tenderId?: string;
}
export const LegalAssistanceSheet = ({
  tenderId
}: LegalAssistanceSheetProps) => {
  const {
    t
  } = useTranslation('legal');
  const {
    toast
  } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [assistanceType, setAssistanceType] = useState<string>("review_compliance");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPremium, setIsPremium] = useState(false);

  // Check premium status when component mounts
  useEffect(() => {
    const checkPremiumStatus = async () => {
      const premium = await isPremiumUser();
      setIsPremium(premium);
    };
    checkPremiumStatus();
  }, []);
  const handleSubmit = async () => {
    if (!message) {
      toast({
        title: "Required Information",
        description: "Please provide details about your request",
        variant: "destructive"
      });
      return;
    }

    // Check if user is trying to use premium features without premium account
    if (assistanceType === "consultation" && !isPremium) {
      toast({
        title: t("premiumFeature"),
        description: t("premiumFeatureRequired"),
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();

      // Save the request to database
      const {
        error: dbError
      } = await supabase.from('legal_assistance_requests').insert({
        user_id: user?.id || null,
        tender_id: tenderId || null,
        assistance_type: assistanceType,
        message,
        status: 'pending'
      });
      if (dbError) throw dbError;

      // Send email notification
      const {
        error: emailError
      } = await supabase.functions.invoke('send-contact-email', {
        body: {
          type: 'legal',
          name: user?.user_metadata?.full_name || 'Anonymous',
          email: user?.email || 'No email provided',
          message,
          assistanceType,
          userId: user?.id // Pass the user ID to the function
        }
      });
      if (emailError) throw emailError;
      toast({
        title: t("requestReceived"),
        description: t("requestReviewMessage")
      });
      setIsOpen(false);
      setMessage("");
      setAssistanceType("review_compliance");
    } catch (error) {
      console.error("Error submitting legal assistance request:", error);
      toast({
        title: "Request not processed",
        description: "There was an issue with your request. Please try again or contact support.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" className="gap-2 bg-white hover:bg-gray-50 text-primary-700 border-primary-200 hover:border-primary-300 text-left px-[19px] py-0 my-0 mx-[1030px]">
          <Scale className="h-4 w-4" />
          {t("requestAssistance")}
        </Button>
      </SheetTrigger>
      <SheetContent className="overflow-y-auto sm:max-w-md">
        <SheetHeader className="mb-6">
          <SheetTitle>{t("legalAssistance")}</SheetTitle>
          <SheetDescription>
            {t("paidServiceDescription")}
          </SheetDescription>
        </SheetHeader>
        
        <div className="space-y-6">
          <div className="space-y-2">
            <Label>{t("assistanceType")}</Label>
            <RadioGroup value={assistanceType} onValueChange={setAssistanceType} className="grid gap-3">
              <Card className={`border cursor-pointer transition-all ${assistanceType === "review_compliance" ? "border-primary-300 bg-primary-50" : ""}`} onClick={() => setAssistanceType("review_compliance")}>
                <CardHeader className="pb-2 py-[29px]">
                  <CardTitle className="text-base flex justify-between">
                    {t("documentReview")}
                    {assistanceType === "review_compliance" && <CheckCircle className="w-4 h-4 text-primary-600" />}
                  </CardTitle>
                  <CardDescription>{t("documentReviewDesc")}</CardDescription>
                </CardHeader>
                
              </Card>
              
              <Card className={`border cursor-pointer transition-all ${!isPremium ? "border-gray-200 bg-gray-50 opacity-70 cursor-not-allowed" : assistanceType === "consultation" ? "border-primary-300 bg-primary-50" : ""}`} onClick={() => {
              if (isPremium) {
                setAssistanceType("consultation");
              }
            }}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex justify-between">
                    <div className="flex items-center">
                      {t("consultation")}
                      {!isPremium && <Badge variant="secondary" className="ml-2 bg-primary-100 text-primary-800">Premium</Badge>}
                    </div>
                    {assistanceType === "consultation" && isPremium && <CheckCircle className="w-4 h-4 text-primary-600" />}
                    {!isPremium && <Lock className="w-4 h-4 text-gray-400" />}
                  </CardTitle>
                  <CardDescription className={!isPremium ? "text-gray-400" : ""}>
                    {t("consultationDesc")}
                  </CardDescription>
                </CardHeader>
                <CardFooter className="pt-1 text-xs text-muted-foreground flex justify-between">
                  <span>{t("paidService")}</span>
                  {!isPremium && <span className="text-primary-600">{t("premiumRequired", "Kræver premium")}</span>}
                </CardFooter>
              </Card>
            </RadioGroup>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">{t("requestDetails")}</Label>
            <Textarea id="message" value={message} onChange={e => setMessage(e.target.value)} placeholder={t("requestDetailsPlaceholder")} className="h-32" />
          </div>
          
          <Button onClick={handleSubmit} disabled={isSubmitting || !message} className="w-full gap-2 bg-primary hover:bg-primary-600">
            {isSubmitting ? <>
                <Clock className="h-4 w-4 animate-spin" />
                {t("processing")}
              </> : <>
                {t("submitRequest")}
                <ArrowRight className="h-4 w-4" />
              </>}
          </Button>
          
          <div className="text-xs text-muted-foreground">
            <p>{t("pricingInfoMessage")}</p>
          </div>
        </div>
      </SheetContent>
    </Sheet>;
};